import React, {useState, useEffect, useCallback, useContext} from "react";

import { Badge } from "@churchofjesuschrist/eden-badge";
import {
  NavigationBlockExpandable,
  NavigationBlockExpandableLink,
  NavigationBlockLink,
  NavigationGroup,
  NavigationSubLink,
  SideNavigation,
} from "@churchofjesuschrist/eden-side-navigation";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

import { identifyActiveHref } from "./items-helpers/active-item-helper";
import { buildRootItems } from "./items-helpers/root-items";
import AnalyticsAppContext from "../../app/analytics-app-context";
import {useFeatureFlagCollection} from "../../generic/hooks/use-feature-flag/use-feature-flag";
import NavigationContext from "../../shared/navigation-link/navigation-context";
import UserPermissionContext from "../../utils/permissions/user-permissions-context";
import useManagedStrings from "../../utils/use-managed-strings";
import { useTwilioProvider } from "../twilio-provider/twilio-provider";

const renderNavigationItem = (item, activeHref) => {
  const isActive = item.href === activeHref;

  // Handle top level links
  if (item.type === 'link') {
    return (
      <NavigationBlockLink
        href={item.href}
        active={isActive}
        onClick={item.onClick}
        badge={item.count ? <Badge color="warn">{item.count}</Badge> : null}
      >
        {item.label}
      </NavigationBlockLink>
    );
  }

  // Handle groups with children
  if (item.type === 'group' && item.items) {
    // Determine if this group should be open (if it contains the active item)
    const containsActive = item.items.some(
      subItem => subItem.href === activeHref ||
                (subItem.items?.some(nestedItem => nestedItem.href === activeHref))
    );

    return (
      <NavigationBlockExpandable key={`expandable_${item.id}`} label={item.label} open={containsActive}>
        {item.items.map((subItem, subIndex) => {
          if (subItem.type === 'link') {
            return (
              <NavigationSubLink
                key={subItem.id}
                href={subItem.href}
                onClick={subItem.onClick}
                active={subItem.href === activeHref}
              >
                {subItem.label}
              </NavigationSubLink>
            );
          }

          // Handle nested groups
          if (subItem.type === 'group' && subItem.items) {
            return (
              <NavigationBlockExpandable key={subItem.id} label={subItem.label}>
                {subItem.items.map((nestedItem, nestedIndex) => (
                  <NavigationBlockExpandableLink
                    key={nestedItem.id}
                    href={nestedItem.href}
                    onClick={nestedItem.onClick}
                    active={nestedItem.href === activeHref}
                  >
                    {nestedItem.label}
                  </NavigationBlockExpandableLink>
                ))}
              </NavigationBlockExpandable>
            );
          }

          return null;
        })}
      </NavigationBlockExpandable>
    );
  }

  return null;
};



const Container = styled.div`
  `

const stringGroups = [
  {
    name: "shared",
    keys: [
      "home",
      "personSearch",
      "programAdministration",
      "faculty",
      "programSchedule",
      "buildings",
      "materials",
      "periods",
      "classes",
      "roster",
      "grades",
      "calendar",
      "students",
      "institute",
      "graduation",
      "reports",
      "messaging",
      "generalInformation",
      "AdministrativeTools",
      "CloseTerm",
      "autoScheduler",
      "mergeStudents",
      "employeeDirectory",
      "yearTerm",
      "newsAndEvents",
      "instituteHubs",
      "courseManagement"
    ],
  },
];

const defaultArray = [];
const defaultFunc = () => {};
//any feature flags we need to evaluate for the accordian navigation
//should be declared here.
//This needs to be a constant array or our use effects could loop our flag inquiry
const flagRequests = [];
const AccordionNavigation = () => {
  const routerNavigate = useNavigate();

  const analyticsPageEvent  = useContext(AnalyticsAppContext) ?? defaultFunc;

  const onClick = useCallback(({event,href,isHome}) => {
    if (isHome) {
      analyticsPageEvent("home");
    }
    event.preventDefault();
    routerNavigate(href);
  },[routerNavigate, analyticsPageEvent])

  const [activeHref, setActiveHref] = useState("/");
  const navigation = useContext(NavigationContext);
  const permissions = useContext(UserPermissionContext);
  const {managedStrings} = useManagedStrings(stringGroups);

  const {loadedFlags = defaultArray} = useFeatureFlagCollection(flagRequests);

  /*NOTE: The navigation helper to identify active href can only run in browser.  That means that if we do this in render in a stateless component,
  react gets mad when server side and initial client renders don't match, and things act wonky.
  So this component sets up its actual items and the active href on component did mount and puts them on state to use in render. */
  const [items, setItems] = useState([]);

  //this is the simplest way we can detect when the user navigated somewhere
  //which will mean we need to recalculate the activeHref
  const location = useLocation();
  useEffect(() => {
    const activeHref = identifyActiveHref(items);
    setActiveHref(activeHref);
  },[location, items])

  const {unreadCount} = useTwilioProvider() ?? 0;

  useEffect(() => {
    const builtItems = buildRootItems({navigation, strings:managedStrings, permissions,
      flags:loadedFlags , onClick,
      unreadCount});
    setItems(builtItems);
  },[navigation,permissions, loadedFlags, managedStrings, onClick, unreadCount]);

  return (
    <Container data-id="left-accordion">
      <SideNavigation>
        <NavigationGroup>
          {items.map((item, index) => (<span key={item.id}> {
            renderNavigationItem(item, activeHref)
          }
          </span>))}
        </NavigationGroup>
      </SideNavigation>
    </Container>
  );
}

export default AccordionNavigation;
